import { useMemo } from 'react';
import { Banner } from '@cbhq/cds-web/banner/Banner';
import { Button, ButtonGroup } from '@cbhq/cds-web/buttons';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { Icon } from '@cbhq/cds-web/icons';
import { Box, Grid, HStack, VStack } from '@cbhq/cds-web/layout';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@cbhq/cds-web/tables';
import {
  TextBody,
  TextDisplay3,
  TextTitle1,
  TextTitle3,
  TextTitle4,
} from '@cbhq/cds-web/typography';

import { ReactComponent as EthStaking } from ':cloud/assets/Products/EthStaking.svg';
import { ReactComponent as EthStakingDarkMode } from ':cloud/assets/Products/EthStakingDarkMode.svg';
import { ReactComponent as StakingHeaderImg } from ':cloud/assets/Products/StakingHeader.svg';
import { useAppState } from ':cloud/contexts/AppStateContext';
import { CodeBlockLanguage } from ':cloud/types/ts_types';
import { STAKING_CASE_STUDY, STAKING_DISCORD_LINK, STAKING_DOCS_LINK } from ':cloud/utils/links';
import { openUrlInNewWindow } from ':cloud/utils/openUrlInNewWindow';
import { Layout } from ':cloud/widgets/layout';
import { FULL_PAGE_MAX_WIDTH, MAIN_CONTAINER_MIN_WIDTH } from ':cloud/widgets/product/constants';
import { KeyFeatureItem, KeyFeaturesSection } from ':cloud/widgets/product/KeyFeaturesSection';
import { QuickstartItem } from ':cloud/widgets/product/QuickstartItem';
import { CodeBlock } from ':cloud/widgets/sharedcomponents';

function onExternalLinkPress(url: string) {
  return () => openUrlInNewWindow(url);
}

const backgroundColor = 'rgb(var(--gray90))';
const customCodeStyle = {
  padding: '25px 10px 25px',
  background: backgroundColor,
  overflow: 'clip',
};

const keyFeatures: KeyFeatureItem[] = [
  {
    iconName: 'layerNetworks',
    title: 'Multi-network simplicity',
    description:
      'Simplify staking operations across multiple networks using a single integration for orchestration, rewards, and more.',
    iconType: 'pictogram',
  },
  {
    iconName: 'transferSend',
    title: 'Rich rewards data',
    description:
      'Access to historical and pending rewards data across multiple networks and addresses.',
    iconType: 'pictogram',
  },
  {
    iconName: 'portfolioPerformance',
    title: 'Insightful reporting',
    description:
      'Easily understand and visualize reporting and insights with our companion dashboard.',
    iconType: 'heroSquare',
  },
  {
    iconName: 'cryptoAssets',
    title: 'Testnet availability',
    description:
      'Testnet support for all available protocols makes tackling new integrations a straightforward process.',
    iconType: 'spotSquare',
  },
];

function QuickstartSection() {
  const bashCode = useMemo(() => {
    const bashCodeCommand = `
npm install @coinbase/staking-client-library-ts
`.trim();
    return [
      {
        language: 'bash' as CodeBlockLanguage,
        code: bashCodeCommand,
      },
    ];
  }, []);

  const javascriptCode = useMemo(() => {
    const javascriptCodeCommand = `
import { StakingClient } from '@coinbase/staking-client-library-ts';

// Set your api key name and private key here. Get your keys from here: https://portal.cdp.coinbase.com/access/api
const apiKeyName: string = 'YOUR_API_KEY_NAME';
const apiPrivateKey: string = 'YOUR_API_PRIVATE_KEY';

const client = new StakingClient(apiKeyName, apiPrivateKey);

// Note:
// 1. Make sure your wallet has some testnet ETH for below code to work.
// 2. You can get some Holeksy testnet ETH here https://faucet.quicknode.com/ethereum/holesky.
// 3. The below amount is in WEI.
client.Ethereum.stake('holesky', 'YOUR_WALLET_ADDRESS', '100')
    .then( (workflow) => {
        console.log("unsigned transaction: ", workflow.steps![workflow.currentStepId!].txStepOutput?.unsignedTx);
    })
    .catch((error) => {
        console.log(error);
    });

// Congratulations! You can now sign this unsigned TX & submit it onchain.
// Here is where you would use (ex. https://docs.cdp.coinbase.com/wallet-sdk)
// to have your user sign & submit the staking transaction with their connected wallet.

`.trim();
    return [
      {
        language: 'typescript' as CodeBlockLanguage,
        code: javascriptCodeCommand,
      },
    ];
  }, []);

  const runCmdCode = useMemo(() => {
    const runCommand = `
npx ts-node index.ts
`.trim();
    return [
      {
        language: 'bash' as CodeBlockLanguage,
        code: runCommand,
      },
    ];
  }, []);

  return (
    <VStack bordered borderRadius="rounded" spacing={4} gap={6}>
      <QuickstartItem
        stepNumber={1}
        title="Install Staking API Client"
        codeBlock={
          <CodeBlock
            codeArr={bashCode}
            showLanguageSelect={false}
            showCopy
            customStyle={customCodeStyle}
            headerBackground={backgroundColor}
            enableLineWrap
            title="shell"
          />
        }
      />
      <QuickstartItem
        stepNumber={2}
        title="Stake Partial ETH"
        codeBlock={
          <CodeBlock
            codeArr={javascriptCode}
            showLanguageSelect={false}
            showCopy
            customStyle={customCodeStyle}
            headerBackground={backgroundColor}
            enableLineWrap
            title="index.ts"
          />
        }
      />
      <QuickstartItem
        stepNumber={3}
        title="Run the example"
        codeBlock={
          <CodeBlock
            codeArr={runCmdCode}
            showLanguageSelect={false}
            showCopy
            customStyle={customCodeStyle}
            headerBackground={backgroundColor}
            enableLineWrap
            title="shell"
          />
        }
      />
    </VStack>
  );
}

function ProtocolAvailabilitySection() {
  return (
    <VStack>
      <TextTitle3 as="h3" spacingBottom={3}>
        Features available across our APIs
      </TextTitle3>
      <TextBody as="p" color="foregroundMuted">
        Use features like staking orchestration to generate transactions across protocols via APIs,
        partial ETH staking to unlock ETH staking without minimums using audited contracts, and
        rewards data to provide insights on earned and pending rewards.
      </TextBody>
      <Box spacingTop={4} spacingBottom={7} display="block" width="100%">
        <Table variant="ruled">
          <TableHeader>
            <TableRow backgroundColor="backgroundAlternate">
              <TableCell title="Protocol" />
              <TableCell title="Staking orchestration" />
              <TableCell title="Rewards data" />
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell color="foregroundMuted">
                <VStack height={54} justifyContent="center">
                  <TextBody as="p" color="foregroundMuted">
                    ETH
                  </TextBody>
                </VStack>
              </TableCell>
              <TableCell>
                <HStack alignItems="center" gap={2} maxWidth={365}>
                  <Icon name="close" size="m" color="negative" />
                  <TextBody as="p">Coming soon</TextBody>
                </HStack>
              </TableCell>
              <TableCell>
                <HStack alignItems="center" gap={2} maxWidth={365}>
                  <Icon name="checkmark" size="m" color="positive" />
                  <TextBody as="p">Validator rewards</TextBody>
                </HStack>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell color="foregroundMuted">
                <VStack height={54} justifyContent="center">
                  <TextBody as="p" color="foregroundMuted">
                    Partial ETH
                  </TextBody>
                </VStack>
              </TableCell>
              <TableCell>
                <HStack alignItems="center" gap={2} maxWidth={365}>
                  <Icon name="checkmark" size="m" color="positive" />
                  <TextBody as="p">Mainnet & Testnet</TextBody>
                </HStack>
              </TableCell>
              <TableCell>
                <HStack alignItems="center" gap={2} maxWidth={365}>
                  <Icon name="checkmark" size="m" color="positive" />
                  <TextBody as="p">Partial ETH rewards</TextBody>
                </HStack>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell color="foregroundMuted">
                <VStack height={54} justifyContent="center">
                  <TextBody as="p" color="foregroundMuted">
                    SOL
                  </TextBody>
                </VStack>
              </TableCell>
              <TableCell>
                <HStack alignItems="center" gap={2} maxWidth={365}>
                  <Icon name="checkmark" size="m" color="positive" />
                  <TextBody as="p">Mainnet & Devnet</TextBody>
                </HStack>
              </TableCell>
              <TableCell>
                <HStack alignItems="center" gap={2} maxWidth={365}>
                  <Icon name="checkmark" size="m" color="positive" />
                  <TextBody as="p">Validator rewards</TextBody>
                </HStack>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell color="foregroundMuted">
                <VStack height={54} justifyContent="center">
                  <TextBody as="p" color="foregroundMuted">
                    ATOM
                  </TextBody>
                </VStack>
              </TableCell>
              <TableCell>
                <HStack alignItems="center" gap={2} maxWidth={365}>
                  <Icon name="close" size="m" color="negative" />
                  <TextBody as="p">Not supported</TextBody>
                </HStack>
              </TableCell>
              <TableCell>
                <HStack alignItems="center" gap={2} maxWidth={365}>
                  <Icon name="checkmark" size="m" color="positive" />
                  <TextBody as="p">Validator & Delegator rewards</TextBody>
                </HStack>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    </VStack>
  );
}

export function StakingAPILanding() {
  const { isDarkMode } = useAppState();

  const { isDesktop } = useBreakpoints();
  const responsiveTemplateColumns = useMemo(() => {
    return isDesktop ? '1fr 3fr' : '1fr';
  }, [isDesktop]);

  const headerResponsiveTemplateColumsn = useMemo(() => {
    return isDesktop ? '3fr 1fr' : '1fr';
  }, [isDesktop]);

  return (
    <Layout enableMaxWidth={false}>
      <Layout.MainContainer minWidth={MAIN_CONTAINER_MIN_WIDTH} spacingHorizontal={0} fullWidth>
        <Box display="block" width="100%" minWidth="100%">
          <VStack>
            <VStack>
              <VStack
                flexGrow={1}
                width="100%"
                maxWidth={FULL_PAGE_MAX_WIDTH}
                spacing={9}
                spacingBottom={0}
              >
                <Banner title="Early access product" variant="informational" startIcon="info">
                  This product is currently in preview. You have been granted early access to the
                  product. There is currently a hard limit of 250 requests per day on rewards data.
                </Banner>
              </VStack>
            </VStack>
            <HStack borderedBottom>
              <Grid
                maxWidth={FULL_PAGE_MAX_WIDTH}
                spacing={9}
                gap={4}
                templateColumns={headerResponsiveTemplateColumsn}
              >
                {!isDesktop && (
                  <HStack>
                    <StakingHeaderImg />
                  </HStack>
                )}
                <VStack>
                  <TextDisplay3 as="h1">Take the busywork out of staking operations</TextDisplay3>
                  <TextTitle4 as="h2" color="foregroundMuted" spacingTop={1} spacingBottom={7}>
                    The Coinbase Staking API provides a full-stack solution for enabling staking
                    services and generating reporting across an array of protocols. Clients can now
                    programmatically access our best-in-class staking infrastructure through one
                    unified endpoint.
                  </TextTitle4>

                  <ButtonGroup>
                    <Button
                      variant="primary"
                      endIcon="diagonalUpArrow"
                      onPress={onExternalLinkPress(STAKING_DOCS_LINK)}
                      accessibilityLabel="Get started with Staking + opens in new window"
                    >
                      Get started with Staking
                    </Button>
                    <Button
                      variant="secondary"
                      endIcon="diagonalUpArrow"
                      onPress={onExternalLinkPress(STAKING_DISCORD_LINK)}
                      accessibilityLabel="Discord channel + opens in new window"
                    >
                      Discord channel
                    </Button>
                  </ButtonGroup>
                </VStack>
                {isDesktop && (
                  <HStack justifyContent="flex-end">
                    <StakingHeaderImg />
                  </HStack>
                )}
              </Grid>
            </HStack>
            <VStack borderedBottom>
              <VStack maxWidth={FULL_PAGE_MAX_WIDTH} spacing={10} gap={5} flexGrow={1} width="100%">
                <HStack gap={2}>
                  <TextTitle1 as="h1">Protocol Availability</TextTitle1>
                </HStack>
                <ProtocolAvailabilitySection />
              </VStack>
            </VStack>
            <VStack borderedBottom>
              <VStack maxWidth={FULL_PAGE_MAX_WIDTH} spacing={10} gap={5} flexGrow={1} width="100%">
                <HStack gap={2}>
                  <TextTitle3 as="h1">Quickstart</TextTitle3>
                </HStack>
                <QuickstartSection />
              </VStack>
            </VStack>
            <KeyFeaturesSection features={keyFeatures} />
            <VStack borderedTop spacing={10}>
              <VStack maxWidth={FULL_PAGE_MAX_WIDTH} flexGrow={1} width="80%">
                <Grid gap={4} width="100%" templateColumns={responsiveTemplateColumns}>
                  {isDarkMode ? <EthStakingDarkMode /> : <EthStaking />}
                  <VStack gap={1} alignItems="flex-start">
                    <TextTitle3 as="h1">
                      See how Coinbase Wallet is using the Staking API
                    </TextTitle3>
                    <TextBody as="p" color="foregroundMuted" spacingBottom={2}>
                      Explore how Coinbase Wallet integrated with the Staking API and makes staking
                      accessible to users holding less than 32 ETH in their wallet.
                    </TextBody>
                    <Button
                      variant="secondary"
                      endIcon="diagonalUpArrow"
                      onPress={onExternalLinkPress(STAKING_CASE_STUDY)}
                      accessibilityLabel="Read more + opens in new window"
                    >
                      Learn more
                    </Button>
                  </VStack>
                </Grid>
              </VStack>
            </VStack>
          </VStack>
        </Box>
      </Layout.MainContainer>
    </Layout>
  );
}
